// src/components/PrivacyPolicy.tsx
export default function PrivacyPolicy() {
  return (
    <div className="min-h-screen bg-gray-50 py-8">
      <div className="max-w-4xl mx-auto px-4">
        <div className="bg-white rounded-lg shadow-lg p-8">
          <h1 className="text-3xl font-bold text-gray-800 mb-6">Privacy Policy</h1>
          <p className="text-sm text-gray-600 mb-8">Last updated: {new Date().toLocaleDateString()}</p>
          
          <div className="prose max-w-none space-y-6">
            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">1. Introduction</h2>
              <p className="mb-4 text-gray-700">
                AZTrainers, operated by Michael Mehta LLC ("we", "our", "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, disclose, and safeguard your information when you use our fitness platform that connects customers with personal trainers.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">2. Information We Collect</h2>
              
              <h3 className="text-lg font-medium mb-3 text-gray-800">2.1 Information You Provide</h3>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li><strong>Account Information:</strong> Name, email address, username, password, phone number</li>
                <li><strong>Profile Information:</strong> Bio, profile pictures, fitness goals, experience level</li>
                <li><strong>Trainer Information:</strong> Certifications, specializations, years of experience, rates</li>
                <li><strong>Payment Information:</strong> Processed securely through Stripe (we don't store full payment details)</li>
                <li><strong>Booking Information:</strong> Session details, addresses, special requirements</li>
                <li><strong>Communications:</strong> Messages, reviews, comments, posts, and other content you share</li>
                <li><strong>Support Requests:</strong> Information you provide when contacting customer support</li>
              </ul>

              <h3 className="text-lg font-medium mb-3 text-gray-800">2.2 Information We Collect Automatically</h3>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li><strong>Usage Data:</strong> How you interact with our platform, features used, time spent</li>
                <li><strong>Device Information:</strong> Device type, operating system, browser type</li>
                <li><strong>Log Data:</strong> IP address, access times, pages viewed, referring URLs</li>
                <li><strong>Location Data:</strong> General location for finding nearby trainers (with your consent)</li>
                <li><strong>Cookies and Similar Technologies:</strong> To enhance your experience and analyze usage</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">3. How We Use Your Information</h2>
              <p className="mb-4 text-gray-700">We use your information to:</p>
              
              <h3 className="text-lg font-medium mb-3 text-gray-800">3.1 Provide Our Services</h3>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>Create and manage your account</li>
                <li>Connect customers with suitable trainers</li>
                <li>Process bookings and payments</li>
                <li>Enable communication between users</li>
                <li>Provide customer support</li>
                <li>Send booking confirmations and updates</li>
              </ul>

              <h3 className="text-lg font-medium mb-3 text-gray-800">3.2 Improve and Personalize</h3>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>Recommend trainers based on your preferences</li>
                <li>Personalize your feed and content</li>
                <li>Analyze usage patterns to improve our service</li>
                <li>Develop new features and functionality</li>
              </ul>

              <h3 className="text-lg font-medium mb-3 text-gray-800">3.3 Safety and Security</h3>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>Verify trainer qualifications and identity</li>
                <li>Monitor for fraudulent or inappropriate activity</li>
                <li>Enforce our Terms of Service</li>
                <li>Protect users' safety and security</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">4. Information Sharing</h2>
              <p className="mb-4 text-gray-700">
                We do not sell your personal information. We share information in the following circumstances:
              </p>

              <h3 className="text-lg font-medium mb-3 text-gray-800">4.1 With Trainers and Customers</h3>
              <p className="mb-4 text-gray-700">
                When you book a session, we share necessary information between customers and trainers including names, contact information, session details, and any special requirements or health considerations you choose to share.
              </p>

              <h3 className="text-lg font-medium mb-3 text-gray-800">4.2 Service Providers</h3>
              <p className="mb-4 text-gray-700">
                We work with trusted third-party service providers:
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li><strong>Payment Processing:</strong> Stripe processes payments securely</li>
                <li><strong>Email Services:</strong> For sending booking confirmations and notifications</li>
                <li><strong>Analytics:</strong> To understand how our service is used</li>
                <li><strong>Cloud Storage:</strong> To securely store and backup data</li>
                <li><strong>Customer Support:</strong> To provide help and resolve issues</li>
              </ul>

              <h3 className="text-lg font-medium mb-3 text-gray-800">4.3 Legal Requirements</h3>
              <p className="mb-4 text-gray-700">
                We may disclose information when required by law, to protect our rights, or to ensure user safety.
              </p>

              <h3 className="text-lg font-medium mb-3 text-gray-800">4.4 Business Transfers</h3>
              <p className="mb-4 text-gray-700">
                If Michael Mehta LLC or AZTrainers is acquired or merges with another company, your information may be transferred as part of that transaction.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">5. Data Security</h2>
              <p className="mb-4 text-gray-700">
                We implement appropriate technical and organizational measures to protect your personal information:
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>Encryption of data in transit and at rest</li>
                <li>Regular security assessments and updates</li>
                <li>Access controls and authentication measures</li>
                <li>Secure payment processing through PCI-compliant providers</li>
                <li>Employee training on data protection</li>
              </ul>
              <p className="mb-4 text-gray-700">
                However, no internet transmission is completely secure. While we strive to protect your information, we cannot guarantee absolute security.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">6. Your Rights and Choices</h2>
              
              <h3 className="text-lg font-medium mb-3 text-gray-800">6.1 Access and Update</h3>
              <p className="mb-4 text-gray-700">
                You can access and update your account information through your profile settings at any time.
              </p>

              <h3 className="text-lg font-medium mb-3 text-gray-800">6.2 Data Portability</h3>
              <p className="mb-4 text-gray-700">
                You can request a copy of your personal data in a structured format.
              </p>

              <h3 className="text-lg font-medium mb-3 text-gray-800">6.3 Deletion</h3>
              <p className="mb-4 text-gray-700">
                You can delete your account at any time. Some information may be retained for legal or business purposes.
              </p>

              <h3 className="text-lg font-medium mb-3 text-gray-800">6.4 Communication Preferences</h3>
              <p className="mb-4 text-gray-700">
                You can opt out of marketing communications but will still receive important service-related messages.
              </p>

              <h3 className="text-lg font-medium mb-3 text-gray-800">6.5 Location Services</h3>
              <p className="mb-4 text-gray-700">
                You can disable location sharing in your device settings or account preferences.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">7. Cookies and Tracking</h2>
              <p className="mb-4 text-gray-700">
                We use cookies and similar technologies to:
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>Remember your login and preferences</li>
                <li>Analyze how you use our service</li>
                <li>Provide personalized content and recommendations</li>
                <li>Ensure security and prevent fraud</li>
                <li>Measure the effectiveness of our communications</li>
              </ul>
              <p className="mb-4 text-gray-700">
                You can control cookies through your browser settings, but disabling them may affect some functionality.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">8. Data Retention</h2>
              <p className="mb-4 text-gray-700">
                We retain your information as long as your account is active or as needed to provide services. Specifically:
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li><strong>Account Data:</strong> Until you delete your account</li>
                <li><strong>Booking History:</strong> For tax and legal compliance purposes</li>
                <li><strong>Payment Records:</strong> As required by financial regulations</li>
                <li><strong>Support Communications:</strong> To resolve ongoing issues</li>
                <li><strong>Anonymized Analytics:</strong> May be retained indefinitely</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">9. Children's Privacy</h2>
              <p className="mb-4 text-gray-700">
                Our service is not intended for children under 18. We do not knowingly collect personal information from children under 18. If we discover we have collected such information, we will delete it promptly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">10. International Users</h2>
              <p className="mb-4 text-gray-700">
                If you access our service from outside the United States, your information may be transferred to and processed in the United States, which may have different privacy laws than your country.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">11. Social Features</h2>
              <p className="mb-4 text-gray-700">
                Our platform includes social features like posts, comments, and following other users. Information you share through these features is public and can be seen by other users. Be mindful of what you share publicly.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">12. Changes to This Policy</h2>
              <p className="mb-4 text-gray-700">
                We may update this Privacy Policy from time to time. We will notify you of significant changes through the service, by email, or by posting a notice on our website. Your continued use after changes indicates your acceptance of the updated policy.
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">13. Contact Us</h2>
              <p className="mb-4 text-gray-700">
                If you have questions about this Privacy Policy or our data practices, please contact us:
              </p>
              <div className="bg-gray-50 p-4 rounded-lg">
                <p className="text-gray-700"><strong>Michael Mehta LLC</strong></p>
                <p className="text-gray-700"><strong>Email:</strong> michaelmehtallc@gmail.com</p>
                <p className="text-gray-700"><strong>Data Protection Officer:</strong> michaelmehtallc@gmail.com</p>
                <p className="text-gray-700"><strong>Address:</strong> 860 N McQueen rd, Chandler, AZ 85225</p>
                <p className="text-gray-700"><strong>Phone:</strong> 480-997-8691</p>
              </div>
            </section>

            <section>
              <h2 className="text-2xl font-semibold mb-4 text-gray-800">14. California Privacy Rights</h2>
              <p className="mb-4 text-gray-700">
                California residents have additional rights under the California Consumer Privacy Act (CCPA):
              </p>
              <ul className="list-disc list-inside mb-4 text-gray-700 space-y-2">
                <li>Right to know what personal information is collected</li>
                <li>Right to delete personal information</li>
                <li>Right to opt-out of the sale of personal information</li>
                <li>Right to non-discrimination for exercising privacy rights</li>
              </ul>
              <p className="mb-4 text-gray-700">
                To exercise these rights, contact us at michaelmehtallc@gmail.com.
              </p>
            </section>
          </div>
          
          
        </div>
      </div>
    </div>
  );
}